import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {IPlayersStore, IPlayer} from "data/stores/players/players.store";
import type {ISquadsStore, ISquad} from "data/stores/squads/squads.store";
import type {
	IContestPlayerStatsStore,
	IContestStat,
} from "data/stores/contest_player_stats/contest_player_stats.store";
import type {IPlayerStatsStore, IPlayerStat} from "data/stores/player_stats/player_stats.store";
import {observable, makeAutoObservable, computed} from "mobx";
import {Empty} from "data/types/global";
import type {ITournamentsStore, ITournament} from "data/stores/tournaments/tournaments.store";
import {mapValues, defaultTo, map} from "lodash";
import {ContestStatus, RosterPlayStatus} from "data/enums";
import {TournamentStatus} from "data/enums/torunaments";

export interface IGamePlayer extends IPlayer {
	squad?: Empty<ISquad>;
	opponent?: Empty<ISquad>;
	contestStats?: Empty<IContestStat>;
	playerStats?: Empty<IPlayerStat>;
	versusOpponent?: Empty<string>;
	hasMultiplier?: Empty<boolean>;
	scoreStatus?: RosterPlayStatus;
}

export interface IPlayerCalculations {
	isLoading(contestId: number): boolean;
	getGamePlayerById(playerId: number, contestId?: number): Empty<IGamePlayer>;
	clearCache(contestId: number): void;
	clearFullCache(contestId: number): void;
	calculatePlayers(): void;
}

@injectable()
export class PlayerCalculations implements IPlayerCalculations {
	@observable private _statsRequest: Record<
		string,
		ReturnType<IContestPlayerStatsStore["fetchStatsSafe"]> | undefined
	> = {};
	@observable _playersByContestId: Record<string, Record<string, IGamePlayer>> = {};
	constructor(
		@inject(Bindings.PlayersStore) private _playersStore: IPlayersStore,
		@inject(Bindings.SquadsStore) private _squadsStore: ISquadsStore,
		@inject(Bindings.ContestPlayerStatsStore)
		private _contestPlayerStatsStore: IContestPlayerStatsStore,
		@inject(Bindings.PlayerStatsStore) private _playerStatsStore: IPlayerStatsStore,
		@inject(Bindings.TournamentsStore) private _tournamentsStore: ITournamentsStore
	) {
		makeAutoObservable(this);
	}

	isLoading(contestId: number): boolean {
		return !!this._statsRequest[contestId];
	}

	getPlayer(playerId: number) {
		return this._playersStore.listById[playerId];
	}

	getSquad(player: IPlayer) {
		return this._squadsStore.getSquadById(player.squadId);
	}

	getPlayerStats(player: IPlayer) {
		return this._playerStatsStore.getStatsByPlayerId(player.id);
	}

	getContestStats(player: IPlayer, contestId: number): IContestStat | undefined {
		const contestStats = this._contestPlayerStatsStore.statsHistory[contestId];

		if (!contestStats) {
			return;
		}

		const playerStats = contestStats.find((it) => it.playerId === player.id);
		if (!playerStats) {
			return;
		}
		if (playerStats.pointsMultiplier > 1) {
			return {
				...playerStats,
				fantasyPoints: playerStats.pointsMultiplier * playerStats.fantasyPoints,
				projectedPoints: playerStats.pointsMultiplier * playerStats.projectedPoints,
			};
		}

		return playerStats;
	}

	getGamePlayerById(playerId: number, contestId?: number) {
		if (!contestId) {
			return this.getPlayer(playerId);
		}

		if (!this._playersByContestId[contestId]) {
			void this.fetchDataForContest(contestId);
		}
		return this._playersByContestId[contestId]?.[playerId];
	}

	getTournament(player: IPlayer, contestId: number) {
		if (!player || !contestId) {
			return;
		}

		return this._tournamentsStore.list.find(
			(it) =>
				it.contestId === contestId &&
				[it.homeSquadId, it.awaySquadId].includes(player.squadId)
		);
	}

	getIsSquadAtHome(player: IPlayer, contestId: number) {
		return this.getTournament(player, contestId)?.homeSquadId === player.squadId;
	}

	getVersusText(player: IPlayer, contestId: number) {
		return this.getIsSquadAtHome(player, contestId) ? "vs" : "@";
	}

	getVersusOpponentSquad(player: IPlayer, contestId: number) {
		const opponentSquad = this.getOpponent(player, contestId)?.abbreviation;
		if (!opponentSquad) {
			return;
		}
		return `${this.getVersusText(player, contestId)} ${opponentSquad}`;
	}

	getOpponent(player: IPlayer, contestId: number) {
		const contestStatsOpponentSquadId = this.getContestStats(
			player,
			contestId
		)?.opponentSquadId;
		if (!contestStatsOpponentSquadId) {
			return;
		}
		return this._squadsStore.getSquadById(contestStatsOpponentSquadId);
	}

	private async fetchDataForContest(contestId: number) {
		if (this._statsRequest[contestId]) {
			await this._statsRequest[contestId];
			return;
		}

		this._statsRequest[contestId] = this._contestPlayerStatsStore
			.fetchStatsSafe(contestId)
			.then(() => {
				this.calculatePlayers();
				this._statsRequest[contestId] = undefined;
			});
	}

	calculatePlayers() {
		let playersWithContestId: Record<string, Record<string, IGamePlayer>> = {};
		map(this._contestPlayerStatsStore.statsHistory, (stats, id) => {
			const contestId = Number(id);
			playersWithContestId[contestId] = {};
			stats.forEach((it) => {
				const {playerId} = it;
				if (!contestId) {
					return this.getPlayer(playerId);
				}
				const gamePlayer = playersWithContestId[contestId]?.[playerId];

				if (!gamePlayer) {
					const player = this.getPlayer(playerId);
					if (!player) {
						return;
					}
					const newGamePlayer = {
						...player,
						contestStats: this.getContestStats(player, contestId),
						squad: this.getSquad(player),
						tournament: this.getTournament(player, contestId),
						opponent: this.getOpponent(player, contestId),
						versusOpponent: this.getVersusOpponentSquad(player, contestId),
					};
					playersWithContestId = {
						...playersWithContestId,
						[contestId]: {
							...playersWithContestId[contestId],
							[playerId]: {
								scoreStatus: this.getScoreStatus(
									newGamePlayer.tournament,
									newGamePlayer.contestStats
								),
								playerStats: this.getPlayerStats(player),
								hasMultiplier:
									defaultTo(newGamePlayer.contestStats?.pointsMultiplier, 0) > 1,
								...newGamePlayer,
							},
						},
					};
				}
			});
		});

		this._playersByContestId = playersWithContestId;
	}

	@computed
	getScoreStatus(tournament?: ITournament, contestStats?: IContestStat) {
		if (!tournament) {
			return ContestStatus.Open;
		}

		if (contestStats?.isLocked && tournament.status === TournamentStatus.PLAYING) {
			return ContestStatus.Live;
		}

		if (tournament.status === TournamentStatus.COMPLETE) {
			return ContestStatus.Complete;
		}

		return ContestStatus.Open;
	}

	updatePlayers() {
		map(this._playersByContestId, (stats, key) => {
			map(stats, (player, id) => {
				const playerOO = this._playersByContestId[key][id];
				if (this._playersByContestId[key]?.[id]) {
					this._playersByContestId[key][id] = {
						...this._playersByContestId[key][id],
						contestStats: playerOO.contestStats
							? {
									...playerOO.contestStats,
									fantasyPoints: 1000,
								}
							: undefined,
					};
				}
			});
		});
	}

	clearCache(contestId: number) {
		mapValues(this._playersByContestId[contestId], (_value, playerId) => {
			this.getGamePlayerById(+playerId, contestId);
		});
	}

	clearFullCache(contestId: number) {
		this._playersByContestId = {
			...this._playersByContestId,
			[contestId]: undefined,
		};
	}

	static getCommonPlayersStatus(players: Empty<IGamePlayer>[] = []) {
		const isLive = players.some((it) => it?.scoreStatus === RosterPlayStatus.Live);

		if (isLive) {
			return RosterPlayStatus.Live;
		}

		const isComplete = players.some((it) => it?.scoreStatus === RosterPlayStatus.Complete);

		if (isComplete) {
			return RosterPlayStatus.Complete;
		}

		return RosterPlayStatus.Open;
	}
}
